// Cooking channel client. One channel per CookPage mount; connects
// on useEffect, tears down on unmount.

import { Channel } from "phoenix";
import { getSocket } from "./rooms";

export interface CookSkill {
  skill_id: string;
  xp: number;
  level: number;
  xp_into_level: number;
  xp_to_next: number;
}

export interface CookRecipe {
  id: string;
  inputs: string[];
  output: string;
  level: number;
  xp: number;
  seconds: number;
}

export interface CookCooking {
  recipe_id: string;
  started_at: number;
  finishes_at: number;
  seconds: number;
}

export interface CookOven {
  idx: number;
  unlock_level: number;
  unlocked: boolean;
  fuel: number;
  slots: (string | null)[];
  output: string | null;
  cooking: CookCooking | null;
}

export interface CookState {
  level: number;
  ovens: CookOven[];
  skill: CookSkill;
  recipes: CookRecipe[];
}

export interface CookedEvent {
  oven_idx: number;
  recipe_id: string;
  output: string;
}

export interface CollectPayload {
  collected: string;
  oven: CookOven;
  xp_gained: number;
  leveled_up: boolean;
  skill: CookSkill;
}

export type CookEvictionReason =
  | "acquired_by_sleep"
  | "acquired_by_battle"
  | "acquired_by_other_tab"
  | "idle"
  | "test_cleanup";

export type CookErrorReason =
  | "no_coal"
  | "oven_locked"
  | "cannot_light"
  | "unlit"
  | "cooking_in_progress"
  | "output_blocked"
  | "not_enough_fuel"
  | "no_recipe"
  | "bad_slot"
  | "slot_occupied"
  | "slot_empty"
  | "not_food"
  | "not_in_inventory"
  | "no_output"
  | "no_such_oven"
  | "unknown_item"
  | "bad_payload"
  | "timeout"
  | string;

interface JoinHandlers {
  onState: (state: CookState) => void;
  onOvenChanged: (oven: CookOven) => void;
  onCooked: (e: CookedEvent) => void;
  onEvicted: (reason: CookEvictionReason) => void;
  onError: (msg: string) => void;
}

function getTabId(): string {
  if (typeof window === "undefined") return "server";
  try {
    const KEY = "doi_tab_id";
    let v = window.sessionStorage.getItem(KEY);
    if (!v) {
      v =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `t_${Math.random().toString(36).slice(2)}_${Date.now()}`;
      window.sessionStorage.setItem(KEY, v);
    }
    return v;
  } catch {
    return `t_${Math.random().toString(36).slice(2)}`;
  }
}

export type CookOpResult<T> =
  | { ok: true; payload: T }
  | { ok: false; reason: CookErrorReason };

export interface CookOps {
  channel: Channel;
  light: (ovenIdx: number) => Promise<CookOpResult<CookOven>>;
  place: (
    ovenIdx: number,
    slotIdx: number,
    itemId: string,
  ) => Promise<CookOpResult<CookOven>>;
  clear: (ovenIdx: number, slotIdx: number) => Promise<CookOpResult<CookOven>>;
  cook: (ovenIdx: number) => Promise<CookOpResult<CookOven>>;
  collect: (ovenIdx: number) => Promise<CookOpResult<CollectPayload>>;
}

export async function joinCook(
  userId: number,
  handlers: JoinHandlers,
): Promise<CookOps> {
  const socket = await getSocket();
  const channel = socket.channel(`cook:${userId}`, { tab_id: getTabId() });

  channel.on("state", (p: CookState) => handlers.onState(p));
  channel.on("oven_changed", (p: { oven: CookOven }) =>
    handlers.onOvenChanged(p.oven),
  );
  channel.on("cooked", (p: CookedEvent) => handlers.onCooked(p));
  channel.on("evicted", (p: { reason: CookEvictionReason }) =>
    handlers.onEvicted(p.reason),
  );

  await new Promise<void>((resolve, reject) => {
    channel
      .join()
      .receive("ok", () => resolve())
      .receive("error", (resp: { reason?: string }) => {
        handlers.onError(resp?.reason ?? "join_failed");
        reject(new Error(resp?.reason ?? "join_failed"));
      })
      .receive("timeout", () => {
        handlers.onError("timeout");
        reject(new Error("timeout"));
      });
  });

  const op = <T>(name: string, params: object): Promise<CookOpResult<T>> =>
    new Promise((resolve) => {
      channel
        .push(name, params)
        .receive("ok", (payload: T) => resolve({ ok: true, payload }))
        .receive("error", (payload: { reason: CookErrorReason }) =>
          resolve({ ok: false, reason: payload.reason }),
        )
        .receive("timeout", () => resolve({ ok: false, reason: "timeout" }));
    });

  return {
    channel,
    light: (ovenIdx) => op<CookOven>("light", { oven_idx: ovenIdx }),
    place: (ovenIdx, slotIdx, itemId) =>
      op<CookOven>("place", {
        oven_idx: ovenIdx,
        slot_idx: slotIdx,
        item_id: itemId,
      }),
    clear: (ovenIdx, slotIdx) =>
      op<CookOven>("clear", { oven_idx: ovenIdx, slot_idx: slotIdx }),
    cook: (ovenIdx) => op<CookOven>("cook", { oven_idx: ovenIdx }),
    collect: (ovenIdx) =>
      op<CollectPayload>("collect", { oven_idx: ovenIdx }),
  };
}

export type RecipeMatch =
  | { kind: "match"; recipe: CookRecipe }
  | { kind: "locked"; recipe: CookRecipe }
  | { kind: "none" };

/**
 * Client-side recipe matcher: ignores nil slots, sorts inputs as a
 * multiset. Returns:
 *
 *   - `{kind: "match", recipe}` — recipe exists and player level >=
 *     `recipe.level`. Cook button is enabled.
 *   - `{kind: "locked", recipe}` — recipe exists but the player
 *     doesn't have the level yet. Cook button reads
 *     "REQUIRES COOKING LV X".
 *   - `{kind: "none"}` — slots are empty or the input combination
 *     matches no authored recipe at all.
 *
 * Server is still authoritative (`cook` op double-checks); this just
 * controls how the button reads before the player clicks.
 */
export function findRecipeClient(
  slots: (string | null)[],
  recipes: CookRecipe[],
  playerLevel: number,
): RecipeMatch {
  const filled = slots.filter((s): s is string => s !== null).sort();
  if (filled.length === 0) return { kind: "none" };

  for (const r of recipes) {
    const inputs = [...r.inputs].sort();
    if (
      inputs.length === filled.length &&
      inputs.every((k, i) => k === filled[i])
    ) {
      return r.level <= playerLevel
        ? { kind: "match", recipe: r }
        : { kind: "locked", recipe: r };
    }
  }
  return { kind: "none" };
}

/**
 * Set of item ids that appear as an input in at least one recipe.
 * Drives the inventory modal's "selectable" filter: only items that
 * can be used in SOME recipe are clickable. (Player level isn't part
 * of this — placing an apple is fine at level 1 even if apple_pie is
 * locked.)
 */
export function cookableItemIds(recipes: CookRecipe[]): Set<string> {
  const out = new Set<string>();
  for (const r of recipes) for (const id of r.inputs) out.add(id);
  return out;
}
